// ─── src/hooks/useElevate.ts ─────────────────────────────────────────────────

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteElevateApplication,
  getElevateApplications,
  getElevateStats,
  reviewElevateApplication,
  type ElevateParams,
  type ElevateReviewPatch,
} from "../api/elevate";

export const elevateKeys = {
  all: ["elevate"] as const,
  list: (p: ElevateParams) => [...elevateKeys.all, "list", p] as const,
  stats: () => [...elevateKeys.all, "stats"] as const,
};

export const useElevateApplications = (params: ElevateParams) =>
  useQuery({
    queryKey: elevateKeys.list(params),
    queryFn: () => getElevateApplications(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useElevateStats = () =>
  useQuery({
    queryKey: elevateKeys.stats(),
    queryFn: getElevateStats,
    staleTime: 60_000,
  });

export const useReviewElevateApplication = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ElevateReviewPatch }) =>
      reviewElevateApplication(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: elevateKeys.all }),
  });
};

export const useDeleteElevateApplication = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteElevateApplication,
    onSuccess: () => qc.invalidateQueries({ queryKey: elevateKeys.all }),
  });
};
